import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Hotel, User, LayoutList, Calculator, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import dayjs from "dayjs";
import {
	Avatar,
	AvatarImage,
	AvatarFallback,
	Table as UITable,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
	TableCell,
	Skeleton,
	AttachmentGallery,
} from "@hotelise/ui";
import type { GalleryFile } from "@hotelise/ui";

import { cn } from "utils/cn";
import FileUploadService from "services/storage/fileUpload";

import { PropertyReportDataItem } from "components/molecules";

import { ReportTableItemWithTransfer } from "interfaces/reports";
import { getChannelName } from "utils/channels";
import { MAIN_THEME } from "styles/colors";

const DEFAULT_COLUMNS = [
	{ id: 'property', label: 'Property', isVisible: true },
	{ id: 'channel', label: 'Channel', isVisible: true },
	{ id: 'code', label: 'Code', isVisible: true },
	{ id: 'status', label: 'Status', isVisible: true },
	{ id: 'guests', label: 'Guests', isVisible: true },
	{ id: 'checkin', label: 'Checkin', isVisible: true },
	{ id: 'checkout', label: 'Checkout', isVisible: true },
	{ id: 'cleaningPhotos', label: 'Cleaning', isVisible: true },
	{ id: 'currency', label: 'Currency', isVisible: true },
	{ id: 'accommodation', label: 'Accom.', isVisible: true },
	{ id: 'channelFee', label: 'Channel Fee', isVisible: true },
	{ id: 'netAccommodation', label: 'Net Acc.', isVisible: true },
	{ id: 'cleaning', label: 'Cleaning Fee', isVisible: true },
	{ id: 'payout', label: 'Payout', isVisible: true },
	{ id: 'hoteliseHelp', label: 'Hotelise help', isVisible: true },
	{ id: 'ownerIncome', label: 'Owner income', isVisible: true },
	{ id: 'transfer', label: 'Transfer', isVisible: true },
	{ id: 'sender', label: 'Sender', isVisible: true },
];

const SKEL_ROWS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const SKEL_MOBILE_CARDS = ['a', 'b', 'c', 'd', 'e', 'f'];

interface Props {
	isLoading: boolean;
	showProperty?: boolean;
	reservations: any[];
	onSelect: (item: ReportTableItemWithTransfer | null) => void;
	isMobile?: boolean;
}

const COMPACT_COLUMN_IDS = ['property', 'channel', 'code', 'status', 'guests', 'checkin', 'checkout', 'cleaningPhotos', 'ownerIncome', 'sender'];

const ReservationsList = (props: Props): JSX.Element => {
	const { showProperty, reservations, isLoading, onSelect, isMobile = false } = props;
	const [viewMode, setViewMode] = useState<"compact" | "financial">("compact");

	// Cleaning gallery state
	const [cleaningGalleryOpen, setCleaningGalleryOpen] = useState(false);
	const [cleaningGalleryFiles, setCleaningGalleryFiles] = useState<GalleryFile[]>([]);
	const [cleaningGalleryTitle, setCleaningGalleryTitle] = useState('');

	// Signed thumbnail URL cache: S3 key → signed URL
	// Populated lazily at list level to avoid one-Amplify-call-per-row on render
	const [thumbnailCache, setThumbnailCache] = useState<Record<string, string>>({});
	const signingInProgress = useRef<Set<string>>(new Set());

	useEffect(() => {
		const unsignedKeys = reservations
			.map((r) => r._item.cleaningData?.images?.[0]?.url)
			.filter((key): key is string => !!key && !thumbnailCache[key] && !signingInProgress.current.has(key));

		if (!unsignedKeys.length) return;

		// Mark all as in-progress to prevent duplicate concurrent calls
		for (const key of unsignedKeys) signingInProgress.current.add(key);

		let cancelled = false;
		Promise.all(
			unsignedKeys.map(async (key) => {
				try {
					const signed = await FileUploadService.getSignedUrl(key);
					return [key, signed] as [string, string];
				} catch {
					return null;
				} finally {
					signingInProgress.current.delete(key);
				}
			}),
		).then((results) => {
			if (cancelled) return;
			const updates: Record<string, string> = {};
			for (const entry of results) {
				if (entry) updates[entry[0]] = entry[1];
			}
			if (Object.keys(updates).length) {
				setThumbnailCache((prev) => ({ ...prev, ...updates }));
			}
		});

		return () => { cancelled = true; };
	// thumbnailCache intentionally omitted to avoid infinite loop
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reservations]);

	const handleCleaningClick = useCallback(async (item: ReportTableItemWithTransfer) => {
		const images = item._item.cleaningData?.images;
		if (!images?.length) return;

		const propertyName = item.property?.name || item._item.code;
		setCleaningGalleryTitle(`Cleaning — ${propertyName}`);
		setCleaningGalleryFiles([]);
		setCleaningGalleryOpen(true);

		const files = await Promise.all(
			images.map(async (img, index) => {
				try {
					const signedUrl = await FileUploadService.getSignedUrl(img.url);
					return {
						id: `cleaning-list-${item._item.code}-${index}`,
						name: `Cleaning photo ${index + 1}`,
						url: signedUrl,
						size: 0,
						type: 'image/jpeg',
					} as GalleryFile;
				} catch {
					return null;
				}
			}),
		);
		setCleaningGalleryFiles(files.filter(Boolean) as GalleryFile[]);
	}, []);

	const visibleColumns = useMemo(() => {
		return DEFAULT_COLUMNS.filter(col => {
			if (!showProperty && col.id === 'property') return false;
			// In compact mode only show compact set; financial mode shows all except cleaningPhotos goes after checkout
			if (viewMode === 'compact' && !COMPACT_COLUMN_IDS.includes(col.id)) return false;
			return true;
		});
	}, [showProperty, viewMode]);

	const visibleColumnIds = useMemo(() => visibleColumns.map(c => c.id), [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const viewToggle = (
		<div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted w-fit">
			<button
				className={cn(
					"flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
					viewMode === "compact"
						? "bg-background text-foreground shadow-sm"
						: "text-muted-foreground hover:text-foreground"
				)}
				onClick={() => setViewMode("compact")}
			>
				<LayoutList className="h-3.5 w-3.5" />
				Compact
			</button>
			<button
				className={cn(
					"flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
					viewMode === "financial"
						? "bg-background text-foreground shadow-sm"
						: "text-muted-foreground hover:text-foreground"
				)}
				onClick={() => setViewMode("financial")}
			>
				<Calculator className="h-3.5 w-3.5" />
				Financial
			</button>
		</div>
	);

	const renderDesktopSkeleton = () => (
		<UITable>
			<TableHeader>
				<TableRow>
					{visibleColumns.map(col => (
						<TableHead key={col.id}>
							<Skeleton className="h-4 w-full" />
						</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{SKEL_ROWS.map(key => (
					<TableRow key={key}>
						{visibleColumns.map(col => (
							<TableCell key={col.id}>
								<Skeleton className="h-4 w-3/4" />
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
		</UITable>
	);

	const renderMobileSkeleton = () => (
		<div className="flex flex-col gap-3">
			{SKEL_MOBILE_CARDS.map(key => (
				<div key={key} className="border border-border rounded-xl p-4">
					{showProperty && (
						<div className="flex justify-between items-center mb-2">
							<div className="flex items-center gap-2">
								<Skeleton className="h-6 w-6 rounded-md" />
								<Skeleton className="h-4 w-24" />
							</div>
							<Skeleton className="h-5 w-14 rounded-full" />
						</div>
					)}
					<div className="flex justify-between items-center mb-2">
						<Skeleton className="h-4 w-32" />
						<Skeleton className="h-5 w-14 rounded-full" />
					</div>
					<div className="flex justify-between items-center">
						<Skeleton className="h-3 w-44" />
						<Skeleton className="h-4 w-16" />
					</div>
				</div>
			))}
		</div>
	);

	const renderMobileCard = (reservation: ReportTableItemWithTransfer) => {
		const {
			start_date, end_date, guest, payout_price, currency, channel, status,
			code, adults, accommodation, host_service_fee, net_accomdation, cleaning,
			hotelise_help, owner_income, nights,
		} = reservation._item;
		const { _accountHolder, _toTransfer } = reservation._transferDetails;

		const channelName = (() => {
			const name = getChannelName(channel).toLowerCase();
			return name === "airbnb-official" ? "airbnb" : name;
		})();

		const propertyName = (() => {
			const raw = (reservation.property?.name || "").replaceAll("-", " ").replaceAll("_", " ");
			return raw.charAt(0).toUpperCase() + raw.slice(1);
		})();

		const lowerStatus = (status || "n/a").toLowerCase();
		const isCancelled = lowerStatus === "canceled" || lowerStatus === "cancelled";
		const isAccepted = lowerStatus === "accepted";
		const isFinancial = viewMode === 'financial';

		return (
			<div
				key={reservation._item.code}
				onClick={() => onSelect(reservation)}
				className="rounded-lg border bg-card p-3 space-y-2 cursor-pointer active:bg-muted/50 transition-colors"
			>
				{/* Row 1: Avatar | property name (line 1), code + status badge (line 2) | channel badge */}
				{showProperty && isColumnVisible('property') && (
					<div className="flex items-start gap-2">
						<Avatar className="h-7 w-7 rounded-md shrink-0 mt-0.5">
							<AvatarImage src={reservation.property?.picture || ''} alt={propertyName} className="object-cover" />
							<AvatarFallback className="rounded-md text-[9px]">
								{propertyName.slice(0, 2).toUpperCase()}
							</AvatarFallback>
						</Avatar>
						<div className="flex flex-col min-w-0 flex-1">
							<span className="text-sm font-medium leading-tight truncate">{propertyName}</span>
							<div className="flex items-center gap-1.5 mt-0.5">
								{isColumnVisible('code') && (
									<span className="text-xs text-muted-foreground font-mono">{code}</span>
								)}
								{isColumnVisible('status') && (
									<span
										className="px-1.5 py-px rounded-full text-[10px] font-semibold lowercase tracking-wide border shrink-0"
										style={{
											background: isAccepted ? "rgba(55, 203, 123, 0.15)" : isCancelled ? `${MAIN_THEME.CANCEL_COLOR}25` : "rgba(245, 158, 11, 0.15)",
											color: isAccepted ? "#37CB7B" : isCancelled ? MAIN_THEME.CANCEL_COLOR : "#f59e0b",
											borderColor: isAccepted ? "rgba(55, 203, 123, 0.25)" : isCancelled ? `${MAIN_THEME.CANCEL_COLOR}30` : "rgba(245, 158, 11, 0.25)",
										}}
									>
										{lowerStatus}
									</span>
								)}
							</div>
						</div>
						{isColumnVisible('channel') && (
							<span
								className="px-1.5 py-px rounded-full text-[10px] font-semibold lowercase tracking-wide border shrink-0 self-center"
								style={{
									background: "rgba(148, 163, 184, 0.15)",
									color: "hsl(var(--foreground))",
									borderColor: "rgba(148, 163, 184, 0.25)",
								}}
							>
								{channelName}
							</span>
						)}
					</div>
				)}

				{/* Row 1 fallback when showProperty is false */}
				{!showProperty && (isColumnVisible('code') || isColumnVisible('channel') || isColumnVisible('status')) && (
					<div className="flex items-center gap-2">
						{isColumnVisible('code') && (
							<span className="text-xs text-muted-foreground font-mono">{code}</span>
						)}
						{isColumnVisible('status') && (
							<span
								className="px-1.5 py-px rounded-full text-[10px] font-semibold lowercase tracking-wide border shrink-0"
								style={{
									background: isAccepted ? "rgba(55, 203, 123, 0.15)" : isCancelled ? `${MAIN_THEME.CANCEL_COLOR}25` : "rgba(245, 158, 11, 0.15)",
									color: isAccepted ? "#37CB7B" : isCancelled ? MAIN_THEME.CANCEL_COLOR : "#f59e0b",
									borderColor: isAccepted ? "rgba(55, 203, 123, 0.25)" : isCancelled ? `${MAIN_THEME.CANCEL_COLOR}30` : "rgba(245, 158, 11, 0.25)",
								}}
							>
								{lowerStatus}
							</span>
						)}
						{isColumnVisible('channel') && (
							<span
								className="ml-auto px-1.5 py-px rounded-full text-[10px] font-semibold lowercase tracking-wide border shrink-0"
								style={{
									background: "rgba(148, 163, 184, 0.15)",
									color: "hsl(var(--foreground))",
									borderColor: "rgba(148, 163, 184, 0.25)",
								}}
							>
								{channelName}
							</span>
						)}
					</div>
				)}

				{/* Row 2: Guest name + sender */}
				{(isColumnVisible('guests') || isColumnVisible('sender')) && (
					<div className="flex items-center gap-2">
						{isColumnVisible('guests') && (
							<span className="text-sm text-primary font-medium truncate">
								{guest.first_name} {guest.last_name}
							</span>
						)}
						{isColumnVisible('sender') && (
							<div className="ml-auto shrink-0">
								{_accountHolder === 1
									? <Hotel className="h-4 w-4 text-primary" />
									: <User className="h-4 w-4 text-primary" />
								}
							</div>
						)}
					</div>
				)}

				{/* Row 3: Dates + nights + owner income */}
				{(isColumnVisible('checkin') || isColumnVisible('checkout') || isColumnVisible('ownerIncome')) && (
					<div className="flex justify-between items-center">
						<span className="text-xs text-muted-foreground">
							{dayjs(start_date).format("D MMM")} &rarr; {dayjs(end_date).format("D MMM, YYYY")}
							{nights > 0 && <span className="ml-1.5">· {nights} nights</span>}
						</span>
						{isColumnVisible('ownerIncome') && (
							<span className="text-xs font-medium text-foreground tabular-nums">
								{currency} {owner_income}
							</span>
						)}
					</div>
				)}

				{/* Financial breakdown (financial mode only) */}
				{isFinancial && (
					<div className="border-t border-border pt-2 text-xs tabular-nums space-y-1">
						<div className="grid grid-cols-2 gap-x-4 gap-y-1">
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Accom.</span>
								<span>{currency} {accommodation}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Ch. Fee</span>
								<span>{currency} {host_service_fee}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Net Acc.</span>
								<span>{currency} {net_accomdation}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Cleaning</span>
								<span>{currency} {cleaning}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Payout</span>
								<span>{currency} {payout_price}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Hotelise</span>
								<span>{currency} {hotelise_help}</span>
							</div>
						</div>
						<div className="flex justify-end items-center border-t border-border pt-1">
							<div className="flex items-center gap-1">
								<span className="text-foreground font-semibold">Transfer</span>
								{_accountHolder === 1 ? (
									<ArrowDownLeft className="h-3 w-3 text-emerald-500 shrink-0" />
								) : (
									<ArrowUpRight className="h-3 w-3 text-amber-500 shrink-0" />
								)}
								<span className={cn(
									"font-semibold",
									_accountHolder === 1
										? "text-emerald-600 dark:text-emerald-400"
										: "text-amber-600 dark:text-amber-400"
								)}>
									{currency} {_toTransfer}
								</span>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderMobileList = () => {
		if (isLoading) {
			return renderMobileSkeleton();
		}

		if (reservations.length === 0) {
			return (
				<p className="text-sm text-muted-foreground py-4">
					No reservations found.
				</p>
			);
		}

		return (
			<div className="flex flex-col gap-3">
				{reservations.map((reservation: ReportTableItemWithTransfer) =>
					renderMobileCard(reservation)
				)}
			</div>
		);
	};

	const renderReportItemsTable = () => {
		const hasReservations = reservations.length > 0;
		return (
			<div className="mb-4 py-2 overflow-y-auto">
				<div className="flex justify-between items-center mb-5">
					{viewToggle}
				</div>
				<div className="rounded-md border overflow-hidden">
					{isLoading ? (
						renderDesktopSkeleton()
					) : hasReservations ? (
						<UITable>
							<TableHeader>
								<TableRow>
									{isColumnVisible('property') && <TableHead>Property</TableHead>}
									{isColumnVisible('channel') && <TableHead>Channel</TableHead>}
									{isColumnVisible('code') && <TableHead>Code</TableHead>}
									{isColumnVisible('status') && <TableHead>Status</TableHead>}
									{isColumnVisible('guests') && <TableHead>Guests</TableHead>}
									{isColumnVisible('checkin') && <TableHead>Checkin</TableHead>}
									{isColumnVisible('checkout') && <TableHead>Checkout</TableHead>}
									{isColumnVisible('cleaningPhotos') && <TableHead>Cleaning</TableHead>}
									{isColumnVisible('currency') && <TableHead>Currency</TableHead>}
									{isColumnVisible('accommodation') && <TableHead>Accom.</TableHead>}
									{isColumnVisible('channelFee') && <TableHead>Channel Fee</TableHead>}
									{isColumnVisible('netAccommodation') && <TableHead>Net Acc.</TableHead>}
									{isColumnVisible('cleaning') && <TableHead>Cleaning Fee</TableHead>}
									{isColumnVisible('payout') && <TableHead>Payout</TableHead>}
									{isColumnVisible('hoteliseHelp') && <TableHead>Hotelise help</TableHead>}
									{isColumnVisible('ownerIncome') && <TableHead>Owner income</TableHead>}
									{isColumnVisible('transfer') && <TableHead>Transfer</TableHead>}
									{isColumnVisible('sender') && <TableHead>Sender</TableHead>}
								</TableRow>
							</TableHeader>
							<TableBody>
								{reservations.map(
									(reservation: ReportTableItemWithTransfer) => (
										<PropertyReportDataItem
											key={reservation._item.code}
											data={reservation}
											showProperty={showProperty}
											onSelect={onSelect}
											onCleaningClick={handleCleaningClick}
											signedThumbnailUrl={
												reservation._item.cleaningData?.images?.[0]?.url
													? thumbnailCache[reservation._item.cleaningData.images[0].url]
													: undefined
											}
											visibleColumnIds={visibleColumnIds}
											viewMode={viewMode}
										/>
									)
								)}
							</TableBody>
						</UITable>
					) : (
						<UITable>
							<TableHeader>
								<TableRow>
									{isColumnVisible('property') && <TableHead>Property</TableHead>}
									{isColumnVisible('channel') && <TableHead>Channel</TableHead>}
									{isColumnVisible('code') && <TableHead>Code</TableHead>}
									{isColumnVisible('status') && <TableHead>Status</TableHead>}
									{isColumnVisible('guests') && <TableHead>Guests</TableHead>}
									{isColumnVisible('checkin') && <TableHead>Checkin</TableHead>}
									{isColumnVisible('checkout') && <TableHead>Checkout</TableHead>}
									{isColumnVisible('cleaningPhotos') && <TableHead>Cleaning</TableHead>}
									{isColumnVisible('currency') && <TableHead>Currency</TableHead>}
									{isColumnVisible('accommodation') && <TableHead>Accom.</TableHead>}
									{isColumnVisible('channelFee') && <TableHead>Channel Fee</TableHead>}
									{isColumnVisible('netAccommodation') && <TableHead>Net Acc.</TableHead>}
									{isColumnVisible('cleaning') && <TableHead>Cleaning Fee</TableHead>}
									{isColumnVisible('payout') && <TableHead>Payout</TableHead>}
									{isColumnVisible('hoteliseHelp') && <TableHead>Hotelise help</TableHead>}
									{isColumnVisible('ownerIncome') && <TableHead>Owner income</TableHead>}
									{isColumnVisible('transfer') && <TableHead>Transfer</TableHead>}
									{isColumnVisible('sender') && <TableHead>Sender</TableHead>}
								</TableRow>
							</TableHeader>
							<TableBody>
								<TableRow>
									<TableCell colSpan={visibleColumns.length} className="text-center text-sm text-muted-foreground py-8">
										No reservations found.
									</TableCell>
								</TableRow>
							</TableBody>
						</UITable>
					)}
				</div>
			</div>
		);
	};

	const gallery = cleaningGalleryFiles.length > 0 ? (
		<AttachmentGallery
			open={cleaningGalleryOpen}
			onOpenChange={setCleaningGalleryOpen}
			files={cleaningGalleryFiles}
			title={cleaningGalleryTitle}
			initialIndex={0}
		/>
	) : null;

	if (isMobile) {
		return (
			<>
				<div className="py-4 overflow-y-auto pb-24">
					<div className="flex justify-between items-center mb-3">
						{viewToggle}
					</div>
					{renderMobileList()}
				</div>
				{gallery}
			</>
		);
	}

	return (
		<>
			{renderReportItemsTable()}
			{gallery}
		</>
	);
};

export default ReservationsList;
